import type { Alpine } from 'alpinejs';

type AgeGroup = 'CK' | 'JR' | 'IM' | 'SR';
type BirthDateOptions = {
    month: string;
    day: string;
    year: string;
    summary: { cutoffYear: number; clubYear: string; groups: Record<AgeGroup, string> } | null;
};

/** Every picker owns its date; derived ages update together without submitting the form. */
export function registerBirthDate(Alpine: Alpine) {
    Alpine.data('dobPicker', (options: BirthDateOptions) => ({
        dob_month: options.month,
        dob_day: options.day,
        dob_year: options.year,

        get birth(): Date | null {
            if (!this.dob_month || !this.dob_day || !this.dob_year) return null;
            const year = Number(this.dob_year), month = Number(this.dob_month), day = Number(this.dob_day);
            const birth = new Date(year, month - 1, day);
            // Reject impossible and future birthdays instead of rolling dates forward.
            return birth.getFullYear() === year && birth.getMonth() === month - 1
                && birth.getDate() === day && birth <= new Date() ? birth : null;
        },

        get age(): number | null {
            const birth = this.birth;
            if (!birth) return null;
            const today = new Date();
            const beforeBirthday = today.getMonth() < birth.getMonth()
                || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate());
            return today.getFullYear() - birth.getFullYear() - Number(beforeBirthday);
        },

        get clubAge(): number | null {
            const birth = this.birth;
            const summary = options.summary;
            if (!birth || !summary || birth > new Date(summary.cutoffYear, 11, 31)) return null;
            return summary.cutoffYear - birth.getFullYear();
        },

        get ageGroupLabel(): string {
            const age = this.clubAge;
            if (age === null || !options.summary) return 'Unknown';
            const group: AgeGroup | null = age >= 6 && age <= 8 ? 'CK'
                : age >= 9 && age <= 11 ? 'JR'
                : age >= 12 && age <= 14 ? 'IM'
                : age >= 15 && age <= 20 ? 'SR' : null;
            return group ? `${group} · ${options.summary.groups[group]}` : 'Not in a youth age group';
        },
    }));
}
